// Story 10.3 — Inyecta mensajes WS en la query cache de la conversación
// activa para que la vista se actualice sin esperar refetch.
import { useQueryClient } from '@tanstack/react-query';
import { useWsEvent } from '@/hooks/use-ws-event';
import type { ConversationDetail, ConversationMessage } from '@/lib/conversations-types';
import { queryKeys } from '@/lib/query-keys';

interface WsMessagePayload {
  botId?: string | number;
  payload?: {
    id?: string;
    from?: string;
    to?: string;
    type?: string;
    body?: unknown;
    text?: string;
  };
}

function buildLiveMessage(
  envelope: WsMessagePayload,
  direction: 'inbound' | 'outbound',
): ConversationMessage | null {
  const p = envelope.payload;
  if (!p) return null;
  // P-277: prefijar por dirección (igual que el mapper de backend) para
  // evitar colisión de React keys con cualquier mensaje cargado del
  // event_log que comparta `payload.id`.
  const prefix = direction === 'inbound' ? 'in' : 'out';
  const rawId = p.id ?? `live-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const id = `${prefix}-${rawId}`;
  let text: string | undefined;
  if (typeof p.text === 'string') text = p.text;
  else if (typeof p.body === 'string') text = p.body;
  return {
    kind: 'message',
    id,
    eventId: -1,
    direction,
    timestamp: new Date().toISOString(),
    type: 'text',
    text,
  };
}

export function useConversationRealtime(
  botId: number,
  sessionId: number | null,
  userPhone: string | null,
): void {
  const qc = useQueryClient();

  useWsEvent('message:received', (envelope) => {
    if (sessionId == null || !userPhone) return;
    const env = envelope as unknown as WsMessagePayload;
    // P-274: el WS payload tipa `botId: string | number`. El backend hoy
    // emite siempre string, pero coercionar ambos lados defiende contra
    // un futuro change que envíe number y deja el filtro silenciosamente
    // muerto (live updates desaparecen sin error visible).
    if (String(env.botId) !== String(botId)) return;
    if (env.payload?.from !== userPhone) return;
    const msg = buildLiveMessage(env, 'inbound');
    if (!msg) return;
    qc.setQueryData<ConversationDetail | undefined>(
      queryKeys.conversationDetail(botId, sessionId),
      (current) => (current ? { ...current, messages: [...current.messages, msg] } : current),
    );
  });

  useWsEvent('message:sent', (envelope) => {
    if (sessionId == null || !userPhone) return;
    const env = envelope as unknown as WsMessagePayload;
    if (String(env.botId) !== String(botId)) return;
    if (env.payload?.to !== userPhone) return;
    const msg = buildLiveMessage(env, 'outbound');
    if (!msg) return;
    qc.setQueryData<ConversationDetail | undefined>(
      queryKeys.conversationDetail(botId, sessionId),
      (current) => (current ? { ...current, messages: [...current.messages, msg] } : current),
    );
  });
}
